import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getAllExams, 
  getQuizBuilderSubjects,
  getQuizBuilderChapters, 
  // getQuizBuilderTopics, // COMMENTED OUT AS PER REQUIREMENTS
  generateCustomQuiz,
  isSuccessResponse,
  getResponseData,
  getErrorMessage,
  getUserSubscriptionFeatures
} from '../components/apiClient.js';
import { QuizPreviewSummary, QuizGenerationProgress, DifficultySelection, NumberOfQuestions, GenerateButton, ExamSelection, SubjectSelection, PointsWarningModal, ValidationAlert, PointsPolicyBanner, QuotaBanner } from '../components/custom-quiz';
import { QuizConfig, QuizPreview, QuizOption, DifficultyOption } from '../types';

const CustomQuizGenerator: React.FC = () => {
  const navigate = useNavigate();
  
  const [quizConfig, setQuizConfig] = useState<QuizConfig>({
    exam: '',
    subjects: [],
    chapters: [],
    topics: [],
    difficulty: '',
    numberOfQuestions: 20
  });

  const [isQuizGenerated, setIsQuizGenerated] = useState(false);
  const [quizPreview, setQuizPreview] = useState<QuizPreview | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // API Data States
  const [examOptions, setExamOptions] = useState<QuizOption[]>([]);
  const [subjectOptions, setSubjectOptions] = useState<QuizOption[]>([]);
  const [chapterOptions, setChapterOptions] = useState<QuizOption[]>([]);
  const [topicOptions, setTopicOptions] = useState<QuizOption[]>([]);
  
  // Loading States
  const [loadingExams, setLoadingExams] = useState(true);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [loadingChapters, setLoadingChapters] = useState(false);
  // const [loadingTopics, setLoadingTopics] = useState(false); // COMMENTED OUT - Not needed

  // UI States for accordion
  const [expandedSubjects, setExpandedSubjects] = useState<Set<string>>(new Set());
  // const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set()); // COMMENTED OUT - Not needed without topics

  // Separate state for number input to allow proper editing
  const [numberOfQuestionsInput, setNumberOfQuestionsInput] = useState<string>('20');

  // Points warning modal state
  const [showPointsWarning, setShowPointsWarning] = useState(false);

  // Validation alert state
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  // Subscription state
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  const difficultyOptions: DifficultyOption[] = [
    { value: 'Elementary', label: 'Easy', color: '#28a745' },
    { value: 'Moderate', label: 'Medium', color: '#ffc107' },
    { value: 'Advance', label: 'Hard', color: '#dc3545' }
  ];

  // Exam-Subject Mapping Configuration
  // Add your exam ID to subject IDs mapping here
  const examSubjectMapping: Record<string, string[]> = {
    // Example mappings - replace with your actual exam and subject IDs
    '1': ['1','2','3','4', '5', '6', '7', '8', '9', '10'], // Exam ID 1 shows subjects 1, 2, 3
    '2': ['1','2','3','4', '5', '6', '7', '8', '9', '10'], // Exam ID 2 shows subjects 2, 4, 5
    '3': ['1','2','3','4', '5', '6', '7', '8', '9', '10'], // Exam ID 3 shows subjects 1, 3, 6
    '4': ['1','2','3','4', '5', '6', '7', '8', '9', '10'], // Exam ID 3 shows subjects 1, 3, 6
    '5': ['1','2','3','4', '5', '6', '7', '8', '9', '10'], // Exam ID 3 shows subjects 1, 3, 6
    '6': ['1','2','3','4', '5', '6', '7', '8', '9', '10'], // Exam ID 3 shows subjects 1, 3, 6
    '7': ['1','2','3','4', '5', '6', '7', '8', '9', '10'], // Exam ID 3 shows subjects 1, 3, 6
    '8': ['1','2','3','4', '5', '6', '7', '8', '9', '10'], // Exam ID 3 shows subjects 1, 3, 6
    '9': ['1','2','3','4', '5', '6', '7', '8', '9', '10'], // Exam ID 3 shows subjects 1, 3, 6
    '10': ['1','2','3','4', '5', '6', '7', '8', '9', '10'] // Exam ID 3 shows subjects 1, 3, 6


    // Add more mappings as needed
    // Format: 'examId': ['subjectId1', 'subjectId2', ...]
  };

  // Handle exam change and reset subjects/chapters/topics
  const handleExamChange = (examId: string) => {
    setQuizConfig(prev => ({
      ...prev,
      exam: examId,
      subjects: [], // Reset subjects when exam changes
      chapters: [], // Reset chapters when exam changes
      topics: [] // Reset topics when exam changes
    }));
  };

  const loadExams = async () => {
    try {
      setLoadingExams(true);
      const response = await getAllExams();
      if (isSuccessResponse(response)) {
        const exams = getResponseData(response);
        setExamOptions(exams.map((exam: any) => ({
          value: exam.id.toString(),
          label: exam.exam_name,
          id: exam.id
        })));
      } else {
        console.error('Failed to load exams:', getErrorMessage(response));
      }
    } catch (error) {
      console.error('Error loading exams:', error);
    } finally {
      setLoadingExams(false);
    }
  };

  const loadSubjects = async () => {
    try {
      setLoadingSubjects(true);
      const response = await getQuizBuilderSubjects();
      if (isSuccessResponse(response)) {
        const subjects = getResponseData(response);
        setSubjectOptions(subjects.map((subject: any) => ({
          value: subject.id.toString(),
          label: `${subject.subject}`,
          id: subject.id
        })));
      } else {
        console.error('Failed to load subjects:', getErrorMessage(response));
      }
    } catch (error) {
      console.error('Error loading subjects:', error);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const loadChapters = useCallback(async () => {
    try {
      setLoadingChapters(true);
      const allChapters = [];
      
      // Load chapters for all subjects, not just selected ones
      for (const subject of subjectOptions) {
        const response = await getQuizBuilderChapters(parseInt(subject.value));
        if (isSuccessResponse(response)) {
          const subjectChapters = getResponseData(response);
          allChapters.push(...subjectChapters.map((chapter: any) => ({
            value: chapter.id.toString(),
            label: `${chapter.chapter_name}`,
            id: chapter.id,
            subject_id: chapter.subject_id
          })));
        }
      }
      
      setChapterOptions(allChapters);
    } catch (error) {
      console.error('Error loading chapters:', error);
    } finally {
      setLoadingChapters(false);
    }
  }, [subjectOptions]); // Changed dependency from quizConfig.subjects to subjectOptions

  // COMMENTED OUT - Topic loading not needed as per requirements
  // const loadTopics = useCallback(async () => {
  //   try {
  //     setLoadingTopics(true);
  //     const topics = [];
      
  //     for (const chapterId of quizConfig.chapters) {
  //       const response = await getQuizBuilderTopics(parseInt(chapterId));
  //       if (isSuccessResponse(response)) {
  //         const chapterTopics = getResponseData(response);
  //         topics.push(...chapterTopics.map((topic: any) => ({
  //           value: topic.id.toString(),
  //           label: `${topic.topic_name}`,
  //           id: topic.id,
  //           chapter_id: topic.chapter_id
  //         })));
  //       }
  //     }
      
  //     setTopicOptions(topics);
  //   } catch (error) {
  //     console.error('Error loading topics:', error);
  //   } finally {
  //     setLoadingTopics(false);
  //   }
  // }, [quizConfig.chapters]);

  // Load initial data
  useEffect(() => {
    loadExams();
    loadSubjects();
  }, []);

  // Load subscription data
  useEffect(() => {
    const loadSubscription = async () => {
      try {
        setLoadingSubscription(true);
        const response = await getUserSubscriptionFeatures();
        if (response && response.result) {
          setSubscriptionData(response.result);
        }
      } catch (error) {
        console.error('Failed to load subscription data:', error);
      } finally {
        setLoadingSubscription(false);
      }
    };
    loadSubscription();
  }, []);

  // Load chapters when subjects are loaded (preload all chapters)
  useEffect(() => {
    if (subjectOptions.length > 0) {
      loadChapters();
    }
  }, [subjectOptions, loadChapters]);

  // Load topics when chapters change - COMMENTED OUT AS PER REQUIREMENTS
  useEffect(() => {
    // Topic loading disabled as per requirements
    // Reset topics when subjects change
    if (quizConfig.subjects.length === 0) {
      setQuizConfig(prev => ({ ...prev, chapters: [], topics: [] }));
    }
  }, [quizConfig.subjects]);

  const handleSubjectChange = (subject: string) => {
    const isSubjectSelected = quizConfig.subjects.includes(subject);
    
    setQuizConfig(prev => {
      let updatedChapters = prev.chapters;
      
      if (isSubjectSelected) {
        // Subject is being deselected - remove its chapters
        const subjectChapters = chapterOptions.filter(ch => ch.subject_id?.toString() === subject);
        const chapterIds = subjectChapters.map(ch => ch.value);
        updatedChapters = prev.chapters.filter(ch => !chapterIds.includes(ch));
      }
      // If subject is being selected, keep existing chapters (don't reset)
      
      return {
        ...prev,
        subjects: isSubjectSelected
          ? prev.subjects.filter(s => s !== subject)
          : [...prev.subjects, subject],
        chapters: updatedChapters
        // topics reset removed since topics not needed as per requirements
      };
    });
    
    // Auto-expand subject when selected, collapse when deselected
    setExpandedSubjects(prev => {
      const newSet = new Set(prev);
      if (isSubjectSelected) {
        newSet.delete(subject);
      } else {
        newSet.add(subject);
      }
      return newSet;
    });
    
    // Reset chapter expansions when subjects change - COMMENTED OUT as not needed
    // setExpandedChapters(new Set());
  };

  const handleChapterChange = (chapter: string) => {
    setQuizConfig(prev => ({
      ...prev,
      chapters: prev.chapters.includes(chapter)
        ? prev.chapters.filter(c => c !== chapter)
        : [...prev.chapters, chapter]
      // topics reset removed since topics not needed as per requirements
    }));
    
    // Chapter expansion functionality removed since no topics to expand to
  };

  // COMMENTED OUT - Topic handling not needed as per requirements
  // const handleTopicChange = (topic: string) => {
  //   setQuizConfig(prev => ({
  //     ...prev,
  //     topics: prev.topics.includes(topic)
  //       ? prev.topics.filter(t => t !== topic)
  //       : [...prev.topics, topic]
  //   }));
  // };

  // Accordion helper functions
  const toggleSubjectExpansion = (subjectId: string) => {
    setExpandedSubjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subjectId)) {
        newSet.delete(subjectId);
      } else {
        newSet.add(subjectId);
      }
      return newSet;
    });
  };

  // Select All functions
  const handleSelectAllChapters = (subjectId: string, checked: boolean) => {
    const subjectChapters = chapterOptions.filter(ch => ch.subject_id?.toString() === subjectId);
    const chapterIds = subjectChapters.map(ch => ch.value);
    
    setQuizConfig(prev => ({
      ...prev,
      chapters: checked 
        ? Array.from(new Set([...prev.chapters, ...chapterIds]))
        : prev.chapters.filter(ch => !chapterIds.includes(ch)),
      topics: checked ? prev.topics : prev.topics.filter(t => {
        const topic = topicOptions.find(tp => tp.value === t);
        return !chapterIds.includes(topic?.chapter_id?.toString() || '');
      })
    }));
  };

  // COMMENTED OUT - Topic selection functions not needed as per requirements
  // const handleSelectAllTopics = (chapterId: string, checked: boolean) => {
  //   const chapterTopics = topicOptions.filter(tp => tp.chapter_id?.toString() === chapterId);
  //   const topicIds = chapterTopics.map(tp => tp.value);
    
  //   setQuizConfig(prev => ({
  //     ...prev,
  //     topics: checked
  //       ? Array.from(new Set([...prev.topics, ...topicIds]))
  //       : prev.topics.filter(t => !topicIds.includes(t))
  //   }));
  // };

  // Helper functions to check selection states
  const isSubjectFullySelected = (subjectId: string) => {
    const subjectChapters = chapterOptions.filter(ch => ch.subject_id?.toString() === subjectId);
    return subjectChapters.length > 0 && subjectChapters.every(ch => quizConfig.chapters.includes(ch.value));
  };

  // COMMENTED OUT - Topic-related helper function not needed as per requirements
  // const isChapterFullySelected = (chapterId: string) => {
  //   const chapterTopics = topicOptions.filter(tp => tp.chapter_id?.toString() === chapterId);
  //   return chapterTopics.length > 0 && chapterTopics.every(tp => quizConfig.topics.includes(tp.value));
  // };

  const isSubjectPartiallySelected = (subjectId: string) => {
    const subjectChapters = chapterOptions.filter(ch => ch.subject_id?.toString() === subjectId);
    return subjectChapters.some(ch => quizConfig.chapters.includes(ch.value)) && !isSubjectFullySelected(subjectId);
  };

  // COMMENTED OUT - Topic-related helper function not needed as per requirements
  // const isChapterPartiallySelected = (chapterId: string) => {
  //   const chapterTopics = topicOptions.filter(tp => tp.chapter_id?.toString() === chapterId);
  //   return chapterTopics.some(tp => quizConfig.topics.includes(tp.value)) && !isChapterFullySelected(chapterId);
  // };

  const generateQuiz = async () => {
    // Check quota first
    const customQuiz = subscriptionData?.features?.custom_quiz;
    if (customQuiz && customQuiz.quota_exceeded) {
      const planKey = subscriptionData.subscription_plan_key?.toLowerCase();
      const upgradeMessage = planKey === 'free' 
        ? 'You have reached your custom quiz limit. Please upgrade to Basic, Premium, or Pro plan for unlimited quizzes.'
        : 'You have reached your custom quiz limit.';
      setValidationMessage(upgradeMessage);
      setShowValidationAlert(true);
      return;
    }

    if (!quizConfig.exam || quizConfig.subjects.length === 0 || !quizConfig.difficulty) {
      setValidationMessage('Please fill in all required fields: Exam, Subjects, and Difficulty Level');
      setShowValidationAlert(true);
      return;
    }

    // Check if user has less than 20 questions and show warning modal
    if (quizConfig.numberOfQuestions < 20) {
      setShowPointsWarning(true);
      return;
    }

    await proceedWithQuizGeneration();
  };

  const proceedWithQuizGeneration = async () => {
    setIsGenerating(true);
    
    // Scroll to top of page to show progress display
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    try {
      console.log('Current quiz config:', quizConfig);
      
      // Call the actual API
      const response = await generateCustomQuiz(quizConfig);
      
      console.log('Quiz generation response:', response);
      
      if (isSuccessResponse(response)) {
        const quizData = getResponseData(response);
        
        console.log('Full quiz response data:', quizData);
        console.log('quiz_reference from response:', quizData.quiz_reference);
        
        const selectedExam = examOptions.find(e => e.value === quizConfig.exam);
        const selectedSubjects = subjectOptions.filter(s => quizConfig.subjects.includes(s.value));
        
        let description = `A personalized ${quizConfig.difficulty} level quiz covering ${selectedSubjects.map(s => s.label).join(', ')}`;
        
        if (quizConfig.chapters.length > 0) {
          const selectedChapters = chapterOptions.filter(c => quizConfig.chapters.includes(c.value));
          description += ` - Chapters: ${selectedChapters.map(c => c.label).join(', ')}`;
        }
        
        if (quizConfig.topics.length > 0) {
          const selectedTopics = topicOptions.filter(t => quizConfig.topics.includes(t.value));
          description += ` - Topics: ${selectedTopics.map(t => t.label).join(', ')}`;
        }
        
        setQuizPreview({
          title: quizData.quiz_name || `${selectedExam?.label} Custom Quiz`,
          description: description,
          estimatedTime: quizData.estimated_time || `${Math.ceil(quizConfig.numberOfQuestions * 1.0)} minutes`,
          totalMarks: quizData.total_marks || (quizConfig.numberOfQuestions * 4),
          quizReference: quizData.quiz_reference,
          actualQuestions: quizData.total_questions
        });
        
        console.log('Set quizPreview with quizReference:', quizData.quiz_reference);
        
        setIsQuizGenerated(true);
        
        // Refresh subscription data after successful quiz generation
        try {
          const subscriptionResponse = await getUserSubscriptionFeatures();
          if (subscriptionResponse && subscriptionResponse.data) {
            setSubscriptionData(subscriptionResponse.data);
          }
        } catch (error) {
          console.error('Failed to refresh subscription data:', error);
        }
      } else {
        const errorMsg = getErrorMessage(response) || 'Failed to generate quiz';
        console.error('Quiz generation failed:', response);
        alert(`Error: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Quiz generation error:', error);
      alert('An error occurred while generating the quiz. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Modal handlers
  const handleSetTo20Questions = () => {
    setQuizConfig(prev => ({ ...prev, numberOfQuestions: 20 }));
    setNumberOfQuestionsInput('20');
    setShowPointsWarning(false);
    proceedWithQuizGeneration();
  };

  const handleContinueAnyway = () => {
    setShowPointsWarning(false);
    proceedWithQuizGeneration();
  };

  const startQuiz = () => {
    // Navigate to quiz page with quiz reference in URL params
    console.log('Starting quiz with quizReference:', quizPreview?.quizReference);
    if (quizPreview?.quizReference) {
      navigate(`/quiz/${quizPreview.quizReference}`);
    } else {
      console.error('No quiz reference available for navigation');
    }
  };

  const resetForm = () => {
    setQuizConfig({
      exam: '',
      subjects: [],
      chapters: [],
      topics: [],
      difficulty: '',
      numberOfQuestions: 20
    });
    setNumberOfQuestionsInput('20'); // Reset input field as well
    setIsQuizGenerated(false);
    setQuizPreview(null);
    // Reset dependent data
    setChapterOptions([]);
    setTopicOptions([]);
    // Reset accordion states
    setExpandedSubjects(new Set());
    // setExpandedChapters(new Set()); // COMMENTED OUT - Not needed
  };

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '0px' }}>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            {/* Header */}
            <div className="text-center mb-5">
              {/* <h1 className="display-4 fw-bold mb-3">
                <i className="fas fa-cog me-3" style={{ color: '#667eea' }}></i>
                Custom Quiz Generator
              </h1> */}
              <p className="lead text-muted">
                Create personalized quizzes tailored to your preparation needs
              </p>
            </div>

            {/* Points Policy Banner */}
            {/* <PointsPolicyBanner /> */}

            {/* Quota Banner */}
            {/* <QuotaBanner subscriptionData={subscriptionData} loading={loadingSubscription} /> */}

            <div className="row g-4">
              {/* Progress Display while generating */}
              <QuizGenerationProgress isGenerating={isGenerating} />

              {/* Quiz Configuration Form - Only show when not generated and not generating */}
              {!isQuizGenerated && !isGenerating && (
                <div className="col-12">
                  <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                    <div className="card-header bg-transparent border-0 p-4">
                      <h4 className="fw-bold mb-0">
                        <i className="fas fa-sliders-h me-2 text-primary"></i>
                        Quiz Configuration
                      </h4>
                    </div>
                    <div className="card-body p-4">
                    <form>
                      {/* Exam Selection */}
                      <ExamSelection
                        selectedExam={quizConfig.exam}
                        examOptions={examOptions}
                        loadingExams={loadingExams}
                        onExamChange={handleExamChange}
                      />

                      {/* Show other options only after exam selection */}
                      {quizConfig.exam && (
                        <>
                          {/* Subject, Chapter, Topic Hierarchical Selection */}
                          <SubjectSelection
                            selectedExam={quizConfig.exam}
                            subjectOptions={subjectOptions}
                            chapterOptions={chapterOptions}
                            selectedSubjects={quizConfig.subjects}
                            selectedChapters={quizConfig.chapters}
                            loadingSubjects={loadingSubjects}
                            loadingChapters={loadingChapters}
                            expandedSubjects={expandedSubjects}
                            examSubjectMapping={examSubjectMapping}
                            onSubjectChange={handleSubjectChange}
                            onChapterChange={handleChapterChange}
                            onToggleSubjectExpansion={toggleSubjectExpansion}
                            onSelectAllChapters={handleSelectAllChapters}
                            isSubjectFullySelected={isSubjectFullySelected}
                            isSubjectPartiallySelected={isSubjectPartiallySelected}
                          />

                      {/* Difficulty Level */}
                      <DifficultySelection
                        difficultyOptions={difficultyOptions}
                        selectedDifficulty={quizConfig.difficulty}
                        onDifficultyChange={(difficulty) => setQuizConfig(prev => ({ ...prev, difficulty }))}
                      />

                      {/* Number of Questions */}
                      <NumberOfQuestions
                        numberOfQuestionsInput={numberOfQuestionsInput}
                        onInputChange={(value) => setNumberOfQuestionsInput(value)}
                        onBlur={(value) => {
                          const numValue = parseInt(value) || 1;
                          const validValue = Math.max(1, Math.min(60, numValue));
                          setNumberOfQuestionsInput(validValue.toString());
                          setQuizConfig(prev => ({ 
                            ...prev, 
                            numberOfQuestions: validValue 
                          }));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                      />

                      {/* Generate Button */}
                      <GenerateButton
                        isGenerating={isGenerating}
                        onGenerate={generateQuiz}
                        quotaExceeded={subscriptionData?.features?.custom_quiz?.quota_exceeded || false}
                        label="Generate Custom Quiz"
                      />
                        </>
                      )}
                    </form>
                  </div>
                </div>
                </div>
              )}
            </div>

            {/* Quiz Preview/Summary */}
            <QuizPreviewSummary
              isQuizGenerated={isQuizGenerated}
              isGenerating={isGenerating}
              quizPreview={quizPreview}
              quizConfig={quizConfig}
              examOptions={examOptions}
              subjectOptions={subjectOptions}
              chapterOptions={chapterOptions}
              topicOptions={topicOptions}
              difficultyOptions={difficultyOptions}
              onStartQuiz={startQuiz}
              onResetForm={resetForm}
            />
          </div>
        </div>
      </div>

      {/* Points Warning Modal */}
      <PointsWarningModal
        isOpen={showPointsWarning}
        currentQuestions={quizConfig.numberOfQuestions}
        onClose={() => setShowPointsWarning(false)}
        onSetTo20Questions={handleSetTo20Questions}
        onContinueAnyway={handleContinueAnyway}
      />

      <ValidationAlert
        isOpen={showValidationAlert}
        message={validationMessage}
        onClose={() => setShowValidationAlert(false)}
      />
    </div>
  );
};

export default CustomQuizGenerator;
